import React, { useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';

/** Command palette component that allows searching and executing actions
 *
 * - Shows loading indicator after a small delay (300ms)
 * - Allows navigation with arrow keys and selection with Enter
 * - Puts dark overlay behind the palette
 * - Debounces search queries by 300ms
 */
const CommandPalette = props => {
  const { placeholderText, fetchActions, onExecuteAction, isOpen, onClose } = props;

  const [query, setQuery] = useState('');
  const [actions, setActions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep track of the previously selected action's ID so we can
  // preserve selection if it is still present in the newly fetched list
  // This is only updated when the user explicitly selects an action using the arrow keys
  const prevSelectedActionIdRef = useRef(null);

  // For the loading delay
  const loadingDelayRef = useRef(null);

  // For debouncing the search
  const searchDebounceRef = useRef(null);

  // Input ref to focus the input automatically when the palette opens
  const inputRef = useRef(null);

  // Ref for the actions container to handle scrolling
  const actionsContainerRef = useRef(null);

  // Add global ESC key handler
  useEffect(() => {
    const handleGlobalEscape = e => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleGlobalEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleGlobalEscape);
    };
  }, [isOpen, onClose]);

  // Whenever the palette is opened or closed, focus the input and reset state
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    // reset the state
    prevSelectedActionIdRef.current = null;
    setQuery('');
    setActions([]);
    setSelectedIndex(0);
    setIsLoading(false);
    setError(null);
  }, [isOpen]);

  // Effect for scrolling when selectedIndex changes
  useEffect(() => {
    if (!isOpen || actions.length === 0) return;

    // Use requestAnimationFrame to ensure we measure after DOM updates
    requestAnimationFrame(() => {
      if (actionsContainerRef.current) {
        const selectedElement = actionsContainerRef.current.children[selectedIndex];
        if (selectedElement) {
          const containerRect = actionsContainerRef.current.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();

          // Calculate if the element is out of view (no margins)
          const isAboveView = elementRect.top < containerRect.top;
          const isBelowView = elementRect.bottom > containerRect.bottom;

          if (isAboveView || isBelowView) {
            // Calculate the target scroll position
            let targetScrollTop = actionsContainerRef.current.scrollTop;
            if (isAboveView) {
              targetScrollTop += elementRect.top - containerRect.top;
            } else if (isBelowView) {
              targetScrollTop += elementRect.bottom - containerRect.bottom;
            }

            actionsContainerRef.current.scrollTop = targetScrollTop;
          }
        }
      }
    });
  }, [selectedIndex, isOpen, actions.length]);

  // Fetch actions whenever query changes
  useEffect(() => {
    if (!isOpen) return; // only fetch if the palette is open

    // clear old fetch timers/loading/debounce
    if (loadingDelayRef.current) {
      clearTimeout(loadingDelayRef.current);
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setIsLoading(false);
    setError(null);

    // Debounce the actual search
    searchDebounceRef.current = setTimeout(() => {
      // set up a small delay so that we don't get a flicker of loading for quick calls
      loadingDelayRef.current = setTimeout(() => {
        setIsLoading(true);
      }, 300);

      fetchActions(query)
        .then(newActions => {
          if (loadingDelayRef.current) {
            clearTimeout(loadingDelayRef.current);
          }
          setIsLoading(false);
          setError(null);
          setActions(Array.isArray(newActions) ? newActions : []);

          // If the previously selected action is still in the new list, keep it selected
          // Otherwise, select the first item
          if (prevSelectedActionIdRef.current) {
            const indexOfPrev = newActions.findIndex(act => act.id === prevSelectedActionIdRef.current);
            if (indexOfPrev !== -1) {
              setSelectedIndex(indexOfPrev);
            } else {
              setSelectedIndex(0);
            }
          } else {
            setSelectedIndex(0);
          }
        })
        .catch(err => {
          console.error('Error fetching actions:', err);
          if (loadingDelayRef.current) {
            clearTimeout(loadingDelayRef.current);
          }
          setIsLoading(false);
          setActions([]); // Clear actions
          setError('An error occurred.');
        });
    }, 300); // 300ms debounce delay

    return () => {
      // cleanup
      if (loadingDelayRef.current) {
        clearTimeout(loadingDelayRef.current);
      }
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [query, isOpen]);

  // Handle keyboard navigation inside the palette
  function handlePaletteKeyDown(e) {
    if (e.key === 'Escape') {
      // close the palette
      onClose();
    } else if (actions.length === 0) {
      // If there are no actions, ignore arrow keys and enter
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => {
        const newIndex = (prev + 1) % actions.length;
        prevSelectedActionIdRef.current = actions[newIndex].id;
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => {
        const newIndex = (prev - 1 + actions.length) % actions.length;
        prevSelectedActionIdRef.current = actions[newIndex].id;
        return newIndex;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (actions.length > 0) {
        // Execute the action
        onExecuteAction(actions[selectedIndex]);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (actions.length > 0) {
        // Put the selected action as query
        setQuery(actions[selectedIndex].label);
      }
    }
  }

  return (
    <>
      <style>
        {`
          .action-item:hover:not(.selected) {
            background-color: #ebebeb;
          }
        `}
      </style>
      {/* Dark overlay and command palette modal */}
      {isOpen && (
        <div
          style={styles.overlay}
          onClick={e => {
            // Only close if clicking the overlay itself, not its children
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <div style={styles.paletteContainer}>
            <div style={styles.inputContainer}>
              <input
                ref={inputRef}
                style={styles.input}
                placeholder={placeholderText}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handlePaletteKeyDown}
              />
              {isLoading && <CircularProgress size={20} style={styles.loading} />}
            </div>
            {/* Error message */}
            {error && <div style={styles.error}>{error}</div>}
            {/* Actions list */}
            <div
              ref={actionsContainerRef}
              style={{
                ...styles.actionsContainer
              }}
            >
              {actions.map((action, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <div
                    key={action.id}
                    style={{
                      ...styles.actionItem,
                      ...(isSelected ? styles.actionItemSelected : {}),
                      ...(isLoading ? styles.actionItemDisabled : {})
                    }}
                    onClick={() => {
                      onExecuteAction(action);
                    }}
                  >
                    {action.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '100px' // some top padding to position the palette near the top
  },
  paletteContainer: {
    backgroundColor: '#fff',
    width: 'calc(100vw - 20px)', // Set dynamic width
    maxWidth: '800px', // Ensure it does not exceed 800px
    borderRadius: '8px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
    padding: '16px'
  },
  inputContainer: {
    position: 'relative',
    width: '100%',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center'
  },
  input: {
    width: '100%',
    padding: '8px',
    paddingRight: '40px', // Make room for the loading indicator
    fontSize: '16px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    outline: 'none'
  },
  loading: {
    position: 'absolute',
    right: '10px',
    color: '#888'
  },
  actionsContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    paddingTop: '8px'
  },
  actionItem: {
    padding: '6px 8px',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '4px'
  },
  actionItemSelected: {
    backgroundColor: '#e0e0e0',
    color: 'inherit'
  },
  actionItemDisabled: {
    opacity: 0.5
  },
  error: {
    color: 'red',
    fontSize: '14px',
    marginTop: '8px'
  }
};

export default CommandPalette;
